import { createMemoryClient } from "@vidario/api-client/mock";
import { defaultClient, type VidarioClient } from "@vidario/api-client";

import { notifySessionExpired } from "./session-expiry";

/**
 * Picks the API client the app should use at runtime. The MSW gate matches
 * `main.tsx` and `platform/auth.ts` so the API and auth clients stay in
 * lockstep.
 *
 * - **MSW** (`import.meta.env.DEV` and `VITE_USE_MSW !== "false"`): fetch
 *   client against `/api`, intercepted by the service worker started in
 *   `main.tsx`. MSW speaks the camelCase domain model, so key mapping is
 *   off.
 * - **Real backend** (MSW off and `VITE_API_BASE_URL` set): fetch client
 *   against the Django backend, which is snake_case — key mapping on.
 * - **No backend** (MSW off, no `VITE_API_BASE_URL`): synchronous
 *   in-memory client. Used for static demo deploys.
 */
export function resolveClient(): VidarioClient {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const mswEnabled =
    import.meta.env.DEV && import.meta.env.VITE_USE_MSW !== "false";
  if (mswEnabled) {
    return defaultClient("/api", false, notifySessionExpired);
  }
  if (apiBaseUrl) {
    return defaultClient(apiBaseUrl, true, notifySessionExpired);
  }
  return createMemoryClient();
}

/** Synchronous memory-only client for e2e / environments without MSW. */
export function memoryClient(): VidarioClient {
  return createMemoryClient();
}
